import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ShoppingBagIcon,
  HeartIcon,
  StarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ShoppingCartIcon,
  SparklesIcon,
  TruckIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid'

const ShopPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all')
  const [cart, setCart] = useState<number[]>([])

  const categories = ['all', 'wellness', 'beauty', 'lifestyle', 'tech', 'gifts']

  const products = [
    {
      id: 1,
      name: 'Premium Wellness Kit',
      price: 89.99,
      rating: 4.8,
      reviews: 234,
      category: 'wellness',
      badge: 'Best Seller',
      image: ''
    },
    {
      id: 2,
      name: 'Luxury Spa Collection',
      price: 149.99,
      rating: 4.9,
      reviews: 456,
      category: 'beauty',
      badge: 'Premium',
      image: ''
    },
    {
      id: 3,
      name: 'Smart Mood Lighting',
      price: 79.99,
      rating: 4.7,
      reviews: 189,
      category: 'tech',
      badge: 'New',
      image: ''
    },
    {
      id: 4,
      name: 'Romantic Gift Box',
      price: 59.99,
      rating: 4.6,
      reviews: 342,
      category: 'gifts',
      badge: null,
      image: ''
    },
    {
      id: 5,
      name: 'Couples Experience Pack',
      price: 199.99,
      rating: 5.0,
      reviews: 128,
      category: 'lifestyle',
      badge: 'Premium',
      image: ''
    },
    {
      id: 6,
      name: 'Self-Care Essentials',
      price: 45.99,
      rating: 4.5,
      reviews: 567,
      category: 'wellness',
      badge: null,
      image: ''
    }
  ]

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory)

  const addToCart = (id: number) => {
    setCart([...cart, id])
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <ShoppingBagIcon className="h-10 w-10 text-primary-400" />
            <h1 className="text-4xl font-display font-bold">
              Premium <span className="gradient-text">Shop</span>
            </h1>
          </div>
          <p className="text-white/70">Curated products for meaningful connections</p>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-primary flex items-center space-x-2 relative"
        >
          <ShoppingCartIcon className="h-5 w-5" />
          <span>Cart</span>
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {cart.length}
            </span>
          )}
        </motion.button>
      </motion.div>

      {/* Search & Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col md:flex-row gap-4"
      >
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
          <input
            type="text"
            placeholder="Search products..."
            className="input-glass w-full pl-12"
          />
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-glass flex items-center space-x-2"
        >
          <FunnelIcon className="h-5 w-5" />
          <span>Filters</span>
        </motion.button>
      </motion.div>

      {/* Features Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid md:grid-cols-3 gap-6"
      >
        <FeatureBanner
          icon={TruckIcon}
          title="Fast Delivery"
          description="Free shipping on orders over $50"
        />
        <FeatureBanner
          icon={ShieldCheckIcon}
          title="Secure Payment"
          description="Your information is protected"
        />
        <FeatureBanner
          icon={SparklesIcon}
          title="Premium Quality"
          description="Curated by experts"
        />
      </motion.div>

      {/* Categories */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex space-x-3 overflow-x-auto pb-2"
      >
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-6 py-3 rounded-xl font-medium transition-all capitalize whitespace-nowrap ${
              activeCategory === category
                ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            {category}
          </button>
        ))}
      </motion.div>

      {/* Products Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filteredProducts.map((product, index) => (
          <ProductCard
            key={product.id}
            product={product}
            index={index}
            onAddToCart={addToCart}
          />
        ))}
      </motion.div>
    </div>
  )
}

interface FeatureBannerProps {
  icon: React.ComponentType<any>
  title: string
  description: string
}

const FeatureBanner: React.FC<FeatureBannerProps> = ({ icon: Icon, title, description }) => (
  <div className="card-glass p-6 flex items-center space-x-4">
    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center flex-shrink-0">
      <Icon className="h-6 w-6 text-primary-400" />
    </div>
    <div>
      <h3 className="font-bold mb-1">{title}</h3>
      <p className="text-sm text-white/70">{description}</p>
    </div>
  </div>
)

interface ProductCardProps {
  product: any
  index: number
  onAddToCart: (id: number) => void
}

const ProductCard: React.FC<ProductCardProps> = ({ product, index, onAddToCart }) => {
  const [isLiked, setIsLiked] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.03, y: -8 }}
      className="card-glass overflow-hidden group cursor-pointer"
    >
      {/* Product Image */}
      <div className="relative aspect-square bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
        <ShoppingBagIcon className="h-16 w-16 text-white/30" />
        
        {/* Badge */}
        {product.badge && (
          <div className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full text-sm font-bold">
            {product.badge}
          </div>
        )}

        {/* Wishlist Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation()
            setIsLiked(!isLiked)
          }}
          className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur-md rounded-full"
        >
          {isLiked ? (
            <HeartSolid className="h-5 w-5 text-red-500" />
          ) : (
            <HeartIcon className="h-5 w-5" />
          )}
        </motion.button>
      </div>

      {/* Product Info */}
      <div className="p-6">
        <h3 className="font-bold text-lg mb-2">{product.name}</h3>
        
        {/* Rating */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <StarIcon
                key={i}
                className={`h-4 w-4 ${
                  i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-white/30'
                }`}
              />
            ))}
          </div>
          <span className="text-sm text-white/70">
            {product.rating} ({product.reviews})
          </span>
        </div>

        {/* Price & Add to Cart */}
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold text-primary-400">
            ${product.price}
          </span>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation()
              onAddToCart(product.id)
            }}
            className="btn-primary px-4 py-2"
          >
            Add to Cart
          </motion.button>
        </div>
      </div>
    </motion.div>
  )
}

export default ShopPage